import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useApi } from '@/hooks/useAppApi'
import { useAuth } from '@/hooks/useAppAuth'
import { hasPermission, hasAnyPermission, hasAllPermissions } from '@shared/auth/permissions'

interface ProfileResponse {
  permissions: string[]
  scopes: string[]
  roles: { name: string; permissions: string[] }[]
}

interface UsePermissionsReturn {
  permissions: string[]
  isLoading: boolean
  hasPermission: (required: string) => boolean
  hasAnyPermission: (required: string[]) => boolean
  hasAllPermissions: (required: string[]) => boolean
}

export function usePermissions(): UsePermissionsReturn {
  const api = useApi()
  const { isAuthenticated } = useAuth()
  const { user, isInitializing } = api

  const { data: profile } = useQuery<ProfileResponse>({
    queryKey: ['profile', 'permissions'],
    queryFn: (): Promise<ProfileResponse> => api.get('/profile') as Promise<ProfileResponse>,
    enabled: isAuthenticated && !isInitializing,
    staleTime: 5 * 60 * 1000,
  })

  const permissions = useMemo((): Array<string> => {
    const jwtScopes = user?.scopes ?? []
    const dbPermissions = profile?.permissions ?? []
    const roleNames = profile?.roles?.map((r): string => r.name) ?? []
    const combined = new Set([...jwtScopes, ...dbPermissions, ...roleNames])
    return Array.from(combined)
  }, [user, profile])

  return {
    permissions,
    isLoading: isInitializing,
    hasPermission: (required: string): boolean => hasPermission(permissions, required),
    hasAnyPermission: (required: string[]): boolean => hasAnyPermission(permissions, required),
    hasAllPermissions: (required: string[]): boolean => hasAllPermissions(permissions, required),
  }
}
